import { useState } from "react";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, Textarea } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { DialogoDeAcao } from "./DialogoDeAcao";

type Conta = {
  id: string;
  email: string;
  emailVerified: Date | string | null;
  bloqueadoEm: Date | string | null;
  motivoDoBloqueio: string | null;
  temPin: boolean;
  temSenha: boolean;
};
type Acao = null | "trocarEmail" | "bloquear" | "encerrar" | "pin" | "link";

const data = (d: Date | string | null) => (d ? new Date(d).toLocaleString("pt-BR") : "");

/** Situação da conta e o que o admin pode fazer nela — tudo vai pra Auditoria. */
export function ContaEAcesso({ conta }: { conta: Conta }) {
  const utils = trpc.useUtils();
  const [acao, setAcao] = useState<Acao>(null);
  const [email, setEmail] = useState("");
  const [motivo, setMotivo] = useState("");
  const [link, setLink] = useState<string | null>(null);
  const fechar = () => {
    setAcao(null);
    setEmail("");
    setMotivo("");
    setLink(null);
  };
  const feito = (msg: string) => () => {
    toast.success(msg);
    utils.admin.users.getById.invalidate({ id: conta.id });
    utils.admin.auditoria.list.invalidate();
    fechar();
  };
  const erro = (e: { message: string }) => toast.error(e.message);
  const c = trpc.admin.conta;
  const enviarLink = c.enviarLinkDeSenha.useMutation({ onSuccess: feito("Link de redefinição enviado por e-mail."), onError: erro });
  const gerarLink = c.gerarLinkDeSenha.useMutation({ onSuccess: (r) => { setLink(r.url); setAcao("link"); utils.admin.auditoria.list.invalidate(); }, onError: erro });
  const bloquear = c.bloquear.useMutation({ onSuccess: feito("Conta bloqueada e sessões encerradas."), onError: erro });
  const desbloquear = c.desbloquear.useMutation({ onSuccess: feito("Conta desbloqueada."), onError: erro });
  const encerrar = c.encerrarSessoes.useMutation({ onSuccess: feito("Sessões encerradas."), onError: erro });
  const reenviar = c.reenviarVerificacao.useMutation({ onSuccess: feito("Verificação reenviada."), onError: erro });
  const marcar = c.marcarVerificado.useMutation({ onSuccess: feito("E-mail marcado como verificado."), onError: erro });
  const trocar = c.trocarEmail.useMutation({ onSuccess: feito("E-mail trocado — verificação enviada pro novo."), onError: erro });
  const pin = c.resetarPin.useMutation({ onSuccess: feito("PIN resetado — a pessoa cria outro."), onError: erro });

  return (
    <Card className="border-admin-200">
      <CardHeader>
        <CardTitle>Conta e acesso</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <Linha
          titulo="E-mail"
          estado={conta.emailVerified ? `Verificado em ${data(conta.emailVerified)}` : "Não verificado"}
          alerta={!conta.emailVerified}
        >
          {!conta.emailVerified && (
            <>
              <Button size="sm" variant="outline" disabled={reenviar.isPending} onClick={() => reenviar.mutate({ userId: conta.id })}>Reenviar verificação</Button>
              <Button size="sm" variant="outline" disabled={marcar.isPending} onClick={() => marcar.mutate({ userId: conta.id })}>Marcar verificado</Button>
            </>
          )}
          <Button size="sm" variant="outline" onClick={() => setAcao("trocarEmail")}>Trocar e-mail</Button>
        </Linha>
        <Linha
          titulo="Situação"
          estado={conta.bloqueadoEm ? `Bloqueada desde ${data(conta.bloqueadoEm)} — ${conta.motivoDoBloqueio ?? ""}` : "Ativa"}
          alerta={!!conta.bloqueadoEm}
        >
          {conta.bloqueadoEm ? (
            <Button size="sm" variant="outline" disabled={desbloquear.isPending} onClick={() => desbloquear.mutate({ userId: conta.id })}>Desbloquear</Button>
          ) : (
            <Button size="sm" variant="outline" className="border-red-200 text-red-700" onClick={() => setAcao("bloquear")}>Bloquear</Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setAcao("encerrar")}>Encerrar sessões</Button>
        </Linha>
        <Linha titulo="Senha" estado={conta.temSenha ? "Definida" : "Entra só por Google/Microsoft"}>
          <Button size="sm" variant="outline" disabled={enviarLink.isPending} onClick={() => enviarLink.mutate({ userId: conta.id })}>Enviar link por e-mail</Button>
          <Button size="sm" variant="outline" disabled={gerarLink.isPending} onClick={() => gerarLink.mutate({ userId: conta.id })}>Gerar link</Button>
        </Linha>
        <Linha titulo="PIN de sócio" estado={conta.temPin ? "Criado" : "Não criado"}>
          {conta.temPin && <Button size="sm" variant="outline" onClick={() => setAcao("pin")}>Resetar PIN</Button>}
        </Linha>
      </CardContent>

      <DialogoDeAcao aberto={acao === "trocarEmail"} onFechar={fechar} titulo="Trocar e-mail" rotulo="Trocar e-mail"
        descricao={`Hoje: ${conta.email}. O novo fica não verificado e recebe o link de verificação. É com ele que a pessoa passa a entrar.`}
        pronto={/.+@.+\..+/.test(email)} ocupado={trocar.isPending} onConfirmar={() => trocar.mutate({ userId: conta.id, email })}>
        <Label htmlFor="novo-email">Novo e-mail</Label>
        <Input id="novo-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </DialogoDeAcao>

      <DialogoDeAcao aberto={acao === "bloquear"} onFechar={fechar} titulo="Bloquear conta" rotulo="Bloquear" perigosa
        descricao="A pessoa não entra mais e as sessões abertas caem em até um minuto. Os dados ficam intactos."
        pronto={motivo.trim().length >= 3} ocupado={bloquear.isPending} onConfirmar={() => bloquear.mutate({ userId: conta.id, motivo })}>
        <Label htmlFor="motivo">Motivo (fica na auditoria)</Label>
        <Textarea id="motivo" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      </DialogoDeAcao>

      <DialogoDeAcao aberto={acao === "encerrar"} onFechar={fechar} titulo="Encerrar sessões" rotulo="Encerrar"
        descricao="Todos os aparelhos em que a pessoa está logada saem em até um minuto. Ela entra de novo normalmente."
        ocupado={encerrar.isPending} onConfirmar={() => encerrar.mutate({ userId: conta.id })} />

      <DialogoDeAcao aberto={acao === "pin"} onFechar={fechar} titulo="Resetar PIN de sócio" rotulo="Resetar PIN"
        descricao="O PIN atual deixa de valer. Na próxima ação de sócio a pessoa cria um novo."
        ocupado={pin.isPending} onConfirmar={() => pin.mutate({ userId: conta.id })} />

      <DialogoDeAcao aberto={acao === "link"} onFechar={fechar} titulo="Link de redefinição" rotulo="Copiar link"
        descricao="Uso único, vale por 1 hora. Mande só pra própria pessoa, por um canal confirmado."
        onConfirmar={() => { if (link) navigator.clipboard.writeText(link).then(() => toast.success("Link copiado.")); }}>
        <p className="flex items-center gap-2 break-all rounded-md bg-zinc-50 p-2 font-mono text-xs"><Copy className="h-3.5 w-3.5 shrink-0" />{link}</p>
      </DialogoDeAcao>
    </Card>
  );
}

function Linha({ titulo, estado, alerta, children }: { titulo: string; estado: string; alerta?: boolean; children?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-100 pb-3 last:border-0 last:pb-0">
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wide text-admin-700">{titulo}</p>
        <p className={alerta ? "text-red-700" : "text-admin-900"}>{estado}</p>
      </div>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}
